import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Send, Settings, Bot, User, Loader2 } from 'lucide-react';
import './App.css';

const API_BASE_URL =  'https://kunaldp379-slangbott.hf.space';

// Debug: Log the API URL being used
console.log('API_BASE_URL:', API_BASE_URL);

function App() {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState({
    temperature: 0.8,
    maxLength: 100,
    topK: 50
  });
  const [isConnected, setIsConnected] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    checkHealth();
  }, []);

  const checkHealth = async () => {
    try {
      console.log('Checking health at:', `${API_BASE_URL}/`);
      const response = await axios.get(`${API_BASE_URL}/`);
      console.log('Health response:', response.data);
      setIsConnected(response.data.status === 'running');
    } catch (error) {
      console.error('Health check failed:', error);
      console.error('Error details:', error.response?.data || error.message);
      setIsConnected(false);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      text: inputMessage,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      // For now, let's try the /api/chat endpoint first
      console.log('Sending message to:', `${API_BASE_URL}/api/chat`);
      const response = await axios.post(`${API_BASE_URL}/api/chat`, {
        message: inputMessage,
        temperature: settings.temperature,
        max_length: settings.maxLength,
        top_k: settings.topK
      });

      console.log('Chat response:', response.data);

      const botMessage = {
        id: Date.now() + 1,
        text: response.data.response,
        sender: 'bot',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      console.error('Error details:', error.response?.data || error.message);
      
      // If /api/chat doesn't exist, show a helpful message
      const errorMessage = {
        id: Date.now() + 1,
        text: `API endpoint /api/chat not found. Your Space is running but the chat endpoint needs to be implemented. Error: ${error.response?.status || error.message}`,
        sender: 'bot',
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([]);
  };

  return (
    <div className="app">
      <div className="chat-container">
        <div className="chat-header">
          <div className="header-content">
            <div className="header-left">
              <Bot className="bot-icon" />
              <h1>SlangBot</h1>
              <div className={`status-indicator ${isConnected ? 'connected' : 'disconnected'}`}>
                {isConnected ? '🟢' : '🔴'}
              </div>
            </div>
            <div className="header-right">
              <button 
                className="settings-btn"
                onClick={() => setShowSettings(!showSettings)}
                title="Settings"
              >
                <Settings size={20} />
              </button>
              <button 
                className="clear-btn"
                onClick={clearChat}
                title="Clear Chat"
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        {showSettings && (
          <div className="settings-panel">
            <h3>Settings</h3>
            <div className="setting-group">
              <label>
                Temperature: {settings.temperature}
                <input
                  type="range"
                  min="0.1"
                  max="2.0"
                  step="0.1"
                  value={settings.temperature}
                  onChange={(e) => setSettings(prev => ({ ...prev, temperature: parseFloat(e.target.value) }))}
                />
              </label>
            </div>
            <div className="setting-group">
              <label>
                Max Length: {settings.maxLength}
                <input
                  type="range"
                  min="10"
                  max="200"
                  step="10"
                  value={settings.maxLength}
                  onChange={(e) => setSettings(prev => ({ ...prev, maxLength: parseInt(e.target.value) }))}
                />
              </label>
            </div>
            <div className="setting-group">
              <label>
                Top-K: {settings.topK}
                <input
                  type="range"
                  min="1"
                  max="100"
                  step="1"
                  value={settings.topK}
                  onChange={(e) => setSettings(prev => ({ ...prev, topK: parseInt(e.target.value) }))}
                />
              </label>
            </div>
          </div>
        )}

        <div className="messages-container">
          {messages.length === 0 ? (
            <div className="welcome-message">
              <Bot size={48} className="welcome-icon" />
              <h2>Welcome to SlangBot!</h2>
              <p>Start a conversation by typing a message below.</p>
            </div>
          ) : (
            messages.map((message) => (
              <div key={message.id} className={`message ${message.sender}`}>
                <div className="message-avatar">
                  {message.sender === 'user' ? <User size={20} /> : <Bot size={20} />}
                </div>
                <div className="message-content">
                  <div className={`message-bubble ${message.isError ? 'error' : ''}`}>
                    {message.text}
                  </div>
                  <div className="message-time">
                    {message.timestamp.toLocaleTimeString()}
                  </div>
                </div>
              </div>
            ))
          )}
          {isLoading && (
            <div className="message bot">
              <div className="message-avatar">
                <Bot size={20} />
              </div>
              <div className="message-content">
                <div className="message-bubble loading">
                  <Loader2 className="loading-spinner" />
                  Thinking...
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="input-container">
          <div className="input-wrapper">
            <textarea
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message here... (Press Enter to send, Shift+Enter for new line)"
              disabled={isLoading}
              rows="1"
            />
            <button
              onClick={sendMessage}
              disabled={!inputMessage.trim() || isLoading}
              className="send-button"
            >
              {isLoading ? <Loader2 className="loading-spinner" /> : <Send size={20} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;

